// ~/components/HighlightSlider.js
import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "~/components/styled/card";
import Image from "~/components/image";

export default function HighlightSlider({ highlights = [] }) {
	const trackRef = useRef(null);
	const [canPrev, setCanPrev] = useState(false);
	const [canNext, setCanNext] = useState(false);

	const updateEnds = useCallback(() => {
		const el = trackRef.current;
		if (!el) return;
		const max = el.scrollWidth - el.clientWidth - 1;
		setCanPrev(el.scrollLeft > 0);
		setCanNext(el.scrollLeft < max);
	}, []);

	useEffect(() => {
		updateEnds();
		const el = trackRef.current;
		if (!el) return;
		el.addEventListener("scroll", updateEnds, { passive: true });
		const ro = new ResizeObserver(updateEnds);
		ro.observe(el);
		return () => {
			el.removeEventListener("scroll", updateEnds);
			ro.disconnect();
		};
	}, [updateEnds]);

	// Scroll exactly one card (+ gap)
	const scrollByCard = (dir = 1) => {
		const el = trackRef.current;
		if (!el) return;
		const firstCard =
			el.querySelector("[data-snap-card]") || el.firstElementChild;
		const styles = getComputedStyle(el);
		const gapPx = parseFloat(styles.gap || styles.columnGap || "0") || 0;
		const step = firstCard ? firstCard.clientWidth + gapPx : el.clientWidth * 0.9;
		el.scrollBy({ left: dir * step, behavior: "smooth" });
	};

	const onKeyDown = (e) => {
		if (e.key === "ArrowRight" && canNext) scrollByCard(+1);
		if (e.key === "ArrowLeft" && canPrev) scrollByCard(-1);
	};

	if (!highlights.length) return null;

	// Arrow styles driven by availability
	const arrowClass = (enabled) =>
		[
			"rounded-full p-2 shadow-sm border transition-colors",
			enabled
				? "bg-gray-200 text-gray-800 border-gray-300 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 cursor-pointer"
				: "bg-white text-gray-300 border-gray-200 cursor-default pointer-events-none",
		].join(" ");

	return (
		<div className="relative">
			{/* Controls — top-right above the track, hidden on mobile */}
			<div className="mb-3 hidden sm:flex justify-end gap-2">
				<button
					type="button"
					aria-label="Previous"
					aria-disabled={!canPrev}
					disabled={!canPrev}
					onClick={() => canPrev && scrollByCard(-1)}
					className={arrowClass(canPrev)}
					title={canPrev ? "More highlights to the left" : "No more to the left"}
				>
					<svg
						viewBox="0 0 24 24"
						className="h-5 w-5"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M15 18l-6-6 6-6" />
					</svg>
				</button>

				<button
					type="button"
					aria-label="Next"
					aria-disabled={!canNext}
					disabled={!canNext}
					onClick={() => canNext && scrollByCard(+1)}
					className={arrowClass(canNext)}
					title={canNext ? "More highlights to the right" : "No more to the right"}
				>
					<svg
						viewBox="0 0 24 24"
						className="h-5 w-5"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M9 6l6 6-6 6" />
					</svg>
				</button>
			</div>

			{/* Track — responsive gaps + matching edge padding for clean peek (keep yours) */}
			<div
				ref={trackRef}
				role="listbox"
				aria-label="highlights"
				tabIndex={0}
				onKeyDown={onKeyDown}
				className="gap-6 px-2 pb-6 flex overflow-x-auto snap-x snap-mandatory scroll-smooth touch-pan-x"
				style={{ scrollBehavior: "smooth" }}
			>
				{highlights.map((light, index) => (
					<div
						key={light.id ?? index}
						data-snap-card
						role="option"
						aria-selected="false"
						className="snap-start flex-none w-5/6 sm:w-1/2 lg:w-1/3"
					>
						{/* Card  layout (unchanged) */}

						<div className="">
							<div className="">
								<Image
									className="rounded-md transform"
									image={light.image}
									alt="Highlight Image"
									loading="lazy"
								/>
							</div>
							<div className="mt-2">
								<h4 className="capitalize font-semibold text-lg">{light.heading}</h4>
							</div>
							<p className="mt-2 text-sm">{light.desc}</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

HighlightSlider.propTypes = {
	highlights: PropTypes.array.isRequired,
};
